import * as React from 'react';
import {useState, useEffect, useMemo} from 'react';
import {
  view,
  createView,
  AnimatePresence,
  animate,
  media,
  toIntent,
  useTokenValue,
  assignProps,
} from '@sail/engine';
import {Layer} from '../layers/Layer';
import {Backdrop} from '../Backdrop';
import {FocusScope} from '../layers/FocusScope';
import {DrawerConfirmationDialog} from '../drawer/DrawerConfirmationDialog';
import {DialogContent} from './DialogContent';
import {DialogFooter} from './DialogFooter';
import {BottomSheetOverlay} from '../BottomSheetOverlay';
import {useOverflowBorders} from '../../hooks/useOverflowBorders';
import {tokens} from '../../tokens';
import {
  bottomSheetAnimation,
  fullscreenSheetStyles,
} from '../../utilStyles/sheet';
import {DialogHeader} from './DialogHeader';
import {DialogContext} from './useDialogContext';
import type {DialogContextType} from './useDialogContext';
import {useNestedSheets} from '../../hooks/useNestedSheets';
import {css} from '../../css';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {useUiConfig} from '../../providers/UiConfigProvider';
import type {DialogProps, Unsafe_Modality} from './DialogProps';

type RenderContentFn = (context: DialogContextType) => React.ReactNode;

const dialogIframeOverrides = toIntent([assignProps(useOverlayIframe)]);

function resolveElement(
  elementOrRender: React.ReactNode | RenderContentFn,
  context: DialogContextType,
): React.ReactNode {
  return typeof elementOrRender === 'function'
    ? elementOrRender(context)
    : elementOrRender;
}

// If the user provides their own instance of the subsection container component
// in their element, render it as-is to avoid re-wrapping it in another container.
// This allows a user to attach a ref to the container element. In the future,
// we will allow users to flag a component as a known type to allow us to treat
// user-defined components as container components as well
// (i.e. `header={<MyDialogHeader />}` won't be wrapped in yet another `DialogHeader`).

// TODO: look into bug related to this function https://jira.corp.stripe.com/projects/SAIL/issues/SAIL-2899?filter=allissues
function renderSubsection(
  element: React.ReactNode,
  Container: React.FC<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  render: (element: React.ReactNode) => React.ReactNode,
) {
  if (!element) {
    return null;
  }

  if (React.isValidElement(element) && element.type === Container) {
    return element;
  }

  return render(element);
}

const desktopAnimation = ({
  easing,
  durationShort,
  durationLong,
  disabled,
}: {
  easing: string;
  durationShort: number;
  durationLong: number;
  disabled: boolean;
}) =>
  toIntent([
    animate.in({
      duration: durationShort,
      opacity: 0,
      transform: 'scale(0.95, 0.95)',
      easing,
      disabled,
    }),
    animate.out({
      duration: durationLong,
      opacity: 0,
      transform: 'scale(0.95, 0.95)',
      easing,
      disabled,
    }),
  ]);

const FULL_SIZE_HEIGHT = `calc(100vh - ${tokens.space.xlarge})`;

// TODO: Remove mobile-related styles when we rollout bottom sheets completely.
// Current implementation matches previous version of Dialog so there are no
// changes when enableBottomSheets is disabled.
export const DialogDesktop = createView<DialogProps>(
  ({
    modality,
    onClose: closeDialogWithoutConfirm,
    children,
    header,
    footer,
    confirmCloseMessages,
    subviews,
    preferredMobileDisplay,
    size,
    ...props
  }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const closeDialog = React.useCallback(() => {
      if (confirmCloseMessages) {
        setShowConfirmation(true);
      } else {
        closeDialogWithoutConfirm();
      }
    }, [closeDialogWithoutConfirm, confirmCloseMessages]);

    const [labelId, registerLabelId] = useState<string>();
    const [descriptionId, registerDescriptionId] = useState<string>();
    const dialogScrollIntents = useOverflowBorders(ref, !header, !footer);
    const uiConfig = useUiConfig();
    const isTouch = !!uiConfig?.isTouch;
    const isSmallScreen = !!uiConfig?.isSmallScreen;
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;

    // don't allow bottom sheet variant if user is not on a touch device
    const finalMobileDisplay =
      // eslint-disable-next-line no-nested-ternary
      !isTouch && isSmallScreen
        ? 'fullscreen-sheet'
        : isTouch && isSmallScreen
        ? preferredMobileDisplay || 'bottom-sheet'
        : 'desktop';
    const {sheetStackProvider} = useNestedSheets(ref, finalMobileDisplay);

    const dialogStyles = css({
      stack: 'y',
      overflow: 'hidden',
      pointerEvents: 'auto',
      backgroundColor: 'surface',
      color: 'text',
      outline: 'none',
      '--dialog-height': '80vh',
      '@supports(height: 80dvh)': {
        '--dialog-height': '80dvh',
      },
      [media.up(tokens.viewport.mobile)]: {
        borderRadius: 'medium',
        width: 'var(--dialog-width)',
        maxWidth: '100vw',
        maxHeight: 'var(--dialog-height)',
        position: 'relative',
        boxShadow: 'overlay',
      },
    });

    const context = useMemo<DialogContextType>(
      () => ({
        closeDialog,
        closeDialogWithoutConfirm,
        registerLabelId,
        registerDescriptionId,
        modality,
      }),
      [closeDialog, closeDialogWithoutConfirm, modality],
    );

    useEffect(() => {
      const dialog = ref.current;
      if (dialog && !dialog.contains(document.activeElement)) {
        dialog.focus();
      }
    }, []);

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        closeDialog && closeDialog();
        e.stopPropagation();
        e.preventDefault();
      }
    }

    const easing = useTokenValue(tokens.animation.easing) as string;

    const durationShort = parseInt(
      useTokenValue(tokens.animation.duration.short) as string,
      10,
    );

    const durationLong = parseInt(
      useTokenValue(tokens.animation.duration.long) as string,
      10,
    );

    return (
      <DialogContext.Provider value={context}>
        <Layer
          inherits={subviews.layer}
          uses={shouldApplyIframeOverrides ? dialogIframeOverrides : undefined}
          name={(modality as Unsafe_Modality) !== 'none' ? 'modal' : undefined}
        >
          {(modality as Unsafe_Modality) !== 'none' ? (
            <Backdrop
              onPress={modality !== 'blocking' ? closeDialog : undefined}
              css={{
                // Override opacity set by Backdrop internally to always render a visible backdrop behind Dialogs, even when they are stacked.
                opacity: 1,
              }}
              uses={[
                animate.in({
                  duration: durationShort,
                  opacity: 0,
                  easing,
                }),
                animate.out({
                  duration: durationLong,
                  opacity: 0,
                  easing,
                }),
              ]}
            />
          ) : null}
          <view.div
            uses={[
              css({
                display: 'grid',
                placeItems: 'center',
                position: 'fixed',
                top: 'var(--sail-layer-margin-top, 0px)',
                bottom: 'var(--sail-layer-margin-bottom, 0px)',
                left: 'space.0',
                right: 'space.0',
                pointerEvents: 'none',
              }),
              dialogScrollIntents,
            ]}
            data-needs-scrollbar-adjustment="true"
          >
            <FocusScope
              autoFocus
              restoreFocus
              contain={(modality as Unsafe_Modality) !== 'none'}
            >
              <view.div
                onKeyDown={modality !== 'blocking' ? onKeyDown : undefined}
                {...props}
                ref={ref}
                tabIndex={-1}
                role={
                  (modality as Unsafe_Modality) !== 'none'
                    ? 'alertdialog'
                    : 'dialog'
                }
                {...(labelId ? {'aria-labelledby': labelId} : {})}
                {...(descriptionId ? {'aria-describedby': descriptionId} : {})}
                aria-modal={
                  (modality as Unsafe_Modality) !== 'none' ? 'true' : undefined
                }
                uses={[
                  bottomSheetAnimation(finalMobileDisplay !== 'bottom-sheet'),
                  desktopAnimation({
                    easing,
                    durationShort,
                    durationLong,
                    disabled: finalMobileDisplay === 'bottom-sheet',
                  }),
                  finalMobileDisplay === 'bottom-sheet'
                    ? sheetStackProvider
                    : null,
                  dialogStyles,
                  ...(size === 'full'
                    ? [
                        css({
                          [media.up(tokens.viewport.mobile)]: {
                            height: FULL_SIZE_HEIGHT,
                            maxHeight: FULL_SIZE_HEIGHT,
                          },
                        }),
                      ]
                    : []),
                  ...(isSmallScreen ? [fullscreenSheetStyles] : []),
                ]}
              >
                <BottomSheetOverlay />
                {renderSubsection(
                  resolveElement(header, context),
                  DialogHeader,
                  (element) => (
                    <DialogHeader title={element} />
                  ),
                )}

                {renderSubsection(
                  resolveElement(children, context),
                  DialogContent,
                  (element) => (
                    <DialogContent inherits={subviews.content}>
                      {element}
                    </DialogContent>
                  ),
                )}

                {renderSubsection(
                  resolveElement(footer, context),
                  DialogFooter,
                  (element) => (
                    <DialogFooter actions={element} />
                  ),
                )}

                <AnimatePresence>
                  {confirmCloseMessages && showConfirmation ? (
                    <DrawerConfirmationDialog
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      messages={confirmCloseMessages}
                      onCancel={() => setShowConfirmation(false)}
                      onClose={() => {
                        setShowConfirmation(false);
                        closeDialogWithoutConfirm();
                      }}
                    />
                  ) : null}
                </AnimatePresence>
              </view.div>
            </FocusScope>
          </view.div>
        </Layer>
      </DialogContext.Provider>
    );
  },
);
